import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, DayPickerRangeProps, DateRange } from "react-day-picker";
import { enUS, fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

export type CalendarRangeProps = DayPickerRangeProps & {
  onClose?: () => void;
  onSelect?: (range: DateRange | undefined) => void;
};

export function CalendarRange({
  className,
  classNames,
  showOutsideDays = true,
  selected,
  onClose,
  ...props
}: CalendarRangeProps) {
  const { locale } = useLanguage();
  const dayPickerLocale = locale === "fr" ? fr : enUS;

  // Gestion du mois affiché (par défaut, le mois actuel)
  const initialMonth = selected?.from ? selected.from : new Date();
  const [month, setMonth] = React.useState<Date>(initialMonth);

  // Gestion du premier jour de la semaine (lu depuis localStorage)
  const [firstDay, setFirstDay] = React.useState(() => {
    const savedFirstDay = localStorage.getItem("calendar-first-day");
    return savedFirstDay ? parseInt(savedFirstDay, 10) : 1;
  });

  // Met à jour le mois si la sélection change
  React.useEffect(() => {
    if (selected?.from) {
      setMonth(selected.from);
    }
  }, [selected]);

  return (
    <DayPicker
      mode="range"
      selected={selected}
      month={month}
      onMonthChange={setMonth}
      onSelect={(range, ...rest) => {
        props.onSelect?.(range, ...rest);
        if (range?.from && onClose) onClose(); // Ferme le popover après sélection
      }}
      showOutsideDays={showOutsideDays}
      locale={dayPickerLocale}
      weekStartsOn={Number(firstDay) as 0 | 1 | 2 | 3 | 4 | 5 | 6}
      className={cn("p-3 pointer-events-auto", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: (props) => <ChevronLeft className="h-4 w-4" {...props} />,
        IconRight: (props) => <ChevronRight className="h-4 w-4" {...props} />,
      }}
      {...props}
    />
  );
}

CalendarRange.displayName = "CalendarRange";
